import { useAppSelector } from 'hooks/redux';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import { accountDiscoverySelectors } from 'app-components/flux/account-discovery';
import { getCommonConfig, getLogger, getRedirectUrl } from 'app-components/utils/core';
import { tracker } from 'app-components/utils/core';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { AccordionProps } from '@mui/material/Accordion';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import type { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';

import Link from '@mui/material/Link';
import StripeConnection from 'assets/images/addFI/StripeConnection.svg';
import ErrorContentView from 'components/Dialogs/AccountDiscovery/ErrorContentView';

const StripeOAuthBaseURL = `${
  getCommonConfig()?.external?.stripe_oauth_authorize_url
}?response_type=code&scope=read_write`;

const log = getLogger('StripeOAuth/index.js');

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(
  () => ({
    '&::before': {
      display: 'none',
    },
  }),
);

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary expandIcon={<ChevronRightIcon />} {...props} />
))(({ theme }) => ({
  flexDirection: 'row-reverse',
  paddingLeft: 0,
  paddingRight: 0,
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  paddingLeft: theme.spacing(4),
  paddingTop: 0,
}));

const getClientIdFromChannel = (channel: string) => {
  if (channel === 'STRIPE_TEST') {
    return getCommonConfig().stripe_client_id_test;
  }
  return getCommonConfig().stripe_client_id_live;
};

type Props = {
  error: { code: string; text: string };
  onSuccess: (data: any) => void;

  scope: string;
  trackingProperties: any;
};

const StripeOAuth = (props: Props) => {
  const { error, onSuccess, scope, trackingProperties } = props;

  const pollTimerRef = useRef<number | undefined>(undefined);
  const oauthWindowRef = useRef<Window | null>(null);

  const [oauthInProgress, setOAuthInProgress] = useState(false);
  const [oauthError, setOAuthError] = useState<{ code: string; text: string } | null>(null);

  const channel = useAppSelector((state) => accountDiscoverySelectors.getChannel(state, { scope }));

  const doFocus = useCallback(() => {
    if (oauthWindowRef.current && !oauthWindowRef.current.closed) {
      oauthWindowRef.current.focus();
    }
  }, []);

  // added mouse actions cause not all browsers will allow one window to change focus on another without user action
  //
  const addFocusEventListeners = useCallback(() => {
    window.addEventListener('click', doFocus);
    window.addEventListener('focus', doFocus);
    window.addEventListener('mousedown', doFocus);
  }, [doFocus]);

  const removeFocusEventListeners = useCallback(() => {
    window.removeEventListener('click', doFocus);
    window.removeEventListener('focus', doFocus);
    window.removeEventListener('mousedown', doFocus);
  }, [doFocus]);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      const expectedOrigin = `${getRedirectUrl()}`;
      if (event.origin === expectedOrigin && event.data?.source === 'oauth_redirection') {
        const { data } = event;

        if (pollTimerRef.current) {
          window.clearInterval(pollTimerRef.current);
          pollTimerRef.current = undefined;
        }
        setOAuthInProgress(false);

        oauthWindowRef.current?.close();
        oauthWindowRef.current = null;

        removeFocusEventListeners();
        window.removeEventListener('message', handleMessage);

        if (data.code) {
          tracker.track(tracker.events.stripeConnectFlowComplete, trackingProperties);
          onSuccess({ channel, code: data.code });
        } else {
          log.error('Stripe connect error', data.error);
          setOAuthError(data.error);
        }
      } else {
        log.error(`Origin mismatch. Expected: ${expectedOrigin}, Received: ${event.origin}`);
      }
    },
    [channel, onSuccess, removeFocusEventListeners, trackingProperties],
  );

  // cleanup things that can cause leaks (if needed) when component unmounts
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        window.clearInterval(pollTimerRef.current);
      }
      removeFocusEventListeners();
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage, removeFocusEventListeners]);

  const doOAuthSignIn = () => {
    const clientId = getClientIdFromChannel(channel ?? '');
    const redirectUrl = `${getRedirectUrl()}/stripe-redirect`;
    const StripeOAuthURL = `${StripeOAuthBaseURL}&client_id=${clientId}&redirect_uri=${redirectUrl}&state=${uuidv4()}`;

    setOAuthInProgress(true);
    const child: Window | null = window.open(StripeOAuthURL, 'windowname1', 'width=768, height=775');

    if (!child) {
      setOAuthError({ code: 'popup_blocked', text: 'Popup was blocked.' });
      setOAuthInProgress(false);
      return;
    }

    oauthWindowRef.current = child;
    window.addEventListener('message', handleMessage);
    addFocusEventListeners();

    pollTimerRef.current = window.setInterval(() => {
      if (oauthWindowRef.current?.closed) {
        if (pollTimerRef.current) {
          window.clearInterval(pollTimerRef.current);
          pollTimerRef.current = undefined;
        }
        removeFocusEventListeners();
        window.removeEventListener('message', handleMessage);
        oauthWindowRef.current = null;

        setOAuthError(null);
        setOAuthInProgress(false);
      }
    }, 100);
  };

  // TODO: If user closes the dialog, can we close oauth-window?
  // TODO: Display Error message if OAuthError is not null

  return (
    <>
      <StdDialogContent
        id='stripe_oauth_dialog'
        sx={{ display: 'flex', flexDirection: 'row', mt: 3, mr: 3, ml: 5, p: 0 }}
      >
        <img src={StripeConnection} alt='Stripe Connection Illustration' style={{ alignSelf: 'flex-start' }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', ml: 5 }}>
          {(error || oauthError) && <ErrorContentView error={error || oauthError} />}
          <Typography variant='titleMedium' sx={{ mt: 3 }}>
            Connect your Quicken and Stripe accounts
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
            <Accordion>
              <AccordionSummary aria-controls='bullet1' id='bullet1'>
                <Typography variant='bodyRegular'>Connect to or create a new Stripe account</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant='paragraphRegular'>
                  You’ll be taken to the Stripe login page, where you can create a new account or sign in to an existing
                  one, securely connecting your account to Quicken.
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary aria-controls='bullet2' id='bullet2'>
                <Typography variant='bodyRegular'>Your data is private</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant='paragraphRegular'>
                  Quicken uses your data to provide a consolidated view of your finances and track payments for your
                  invoices.
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary aria-controls='bullet3' id='bullet3'>
                <Typography variant='bodyRegular'>You&apos;re in control</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant='paragraphRegular'>Unlink your accounts at any time from Settings.</Typography>
              </AccordionDetails>
            </Accordion>
          </Box>
          <Typography variant='paragraphRegular'>
            By continuing, you consent to let Quicken connect to your account in accordance with our&nbsp;
            <Link href={getCommonConfig().external.terms} target='_blank' rel='noopener' underline='none'>
              Terms of Service
            </Link>
            .
          </Typography>
        </Box>
      </StdDialogContent>
      <StdDialogActions
        // sx={{ display: 'flex', flexGrow: 0 }}
        primary={{
          children: 'Connect',
          disabled: oauthInProgress,
          id: 'connectButton',
          onClick: () => {
            tracker.track(tracker.events.stripeConnectFlowStart, trackingProperties);
            doOAuthSignIn();
          },
        }}
      />
    </>
  );
};

export default StripeOAuth;
